import { Form, Input, InputNumber, Select, Space } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { FilterOperatorEnum } from 'domains/appDomain';
import {
  MultiTypeDisplay,
  MultiTypeEnum,
  SkuFilterObj,
} from 'domains/skuDomain';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeFilterObj, normalizeToFormFields } from 'utils/filter';
import { FormItemForFilterValues } from '../common/FormItemForFilterValues';

export const SkuFilterForm = () => {
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const filterObj = useAppSelector((state) => state.sku.filterObj);
  const loading = useAppSelector((state) => state.loading.effects.sku.getSKUs);
  const skuTypes = useAppSelector((state) => state.sku.skuTypes);

  const onFinish = (values: SkuFilterObj) => {
    const filterObj = normalizeFilterObj(values);
    dispatch.sku.saveFilter(filterObj);
  };

  useEffect(() => {
    form.setFieldsValue(normalizeToFormFields(filterObj));
  }, [filterObj, form]);

  const clearFilters = () => {
    dispatch.sku.removeFilter();
    form.resetFields();
  };

  return (
    <RevxSpin spinning={loading}>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item label={t('platform.filter.formLabel.id')}>
          <Form.Item
            noStyle
            name={['id', 'operator']}
            initialValue={FilterOperatorEnum.EQUALS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['id', 'values']}>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              controls={false}
              maxLength={15}
              max={Number.MAX_SAFE_INTEGER}
            />
          </FormItemForFilterValues>
        </Form.Item>

        <Form.Item label={t('platform.sku.columnTitle.skuType')}>
          <Form.Item
            noStyle
            name={['skuTypeId', 'operator']}
            initialValue={FilterOperatorEnum.IN}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['skuTypeId', 'values']}>
            <Select
              mode="multiple"
              showSearch
              filterOption={(value, option) => {
                return String(option?.children)
                  .toLocaleLowerCase()
                  .includes(value.toLocaleLowerCase());
              }}
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              {skuTypes.map((option) => {
                return (
                  <Select.Option value={option.id} key={option.id}>
                    {option.name}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item label={t('platform.sku.columnTitle.multiType')}>
          <Form.Item
            noStyle
            name={['multiType', 'operator']}
            initialValue={FilterOperatorEnum.IN}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['multiType', 'values']}>
            <Select
              mode="multiple"
              showSearch
              filterOption={(value, option) => {
                return String(option?.children)
                  .toLocaleLowerCase()
                  .includes(value.toLocaleLowerCase());
              }}
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              {[
                MultiTypeEnum.BOTH,
                MultiTypeEnum.STANDALONE,
                MultiTypeEnum.WRAPPER,
                MultiTypeEnum.PART,
              ].map((type) => {
                return (
                  <Select.Option value={type} key={type}>
                    {MultiTypeDisplay[type]}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item label={t('platform.filter.formLabel.baseOrOrganization')}>
          <Form.Item
            noStyle
            name={['isBaseSku', 'operator']}
            initialValue={FilterOperatorEnum.EQUALS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['isBaseSku', 'values']}>
            <Select
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              <Select.Option value={true as unknown as string}>
                {t('platform.filter.operator.base')}
              </Select.Option>
              <Select.Option value={false as unknown as string}>
                {t('platform.filter.operator.organization')}
              </Select.Option>
            </Select>
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item label={t('platform.filter.formLabel.name')}>
          <Form.Item
            noStyle
            name={['name', 'operator']}
            initialValue={FilterOperatorEnum.CONTAINS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['name', 'values']}>
            <Input />
          </FormItemForFilterValues>
        </Form.Item>

        <Form.Item>
          <Space>
            <RevxButton
              type="primary"
              htmlType="submit"
              brandLightPurple
              className="admin-filter-btn"
            >
              {t('platform.filter.filterBtnLabel')}
            </RevxButton>
            <RevxButton
              onClick={() => {
                clearFilters();
              }}
              className="admin-clear-filter-btn"
              brandGreen
            >
              {t('platform.filter.clearFilterBtnLabel')}
            </RevxButton>
          </Space>
        </Form.Item>
      </Form>
    </RevxSpin>
  );
};
